import { hashKey, type QueryClient, type QueryKey } from "@tanstack/react-query";

type CustomerWindowOwner = {
  isFresh: () => boolean;
  yield: () => void;
};

/** Keep event suppression scoped to the exact cache owned by a validated window. */
export class CustomerListWindowOwnership {
  private static readonly clients = new WeakMap<QueryClient, Map<string, CustomerWindowOwner>>();

  static register(client: QueryClient, queryKey: QueryKey, owner: CustomerWindowOwner): () => void {
    const owners = this.clients.get(client) ?? new Map<string, CustomerWindowOwner>();
    this.clients.set(client, owners);
    const key = hashKey(queryKey);
    owners.get(key)?.yield();
    owners.set(key, owner);
    return () => {
      if (owners.get(key) === owner) {
        owners.delete(key);
      }
    };
  }

  static shouldRefresh(
    client: QueryClient,
    queryKey: QueryKey,
    reasons: ReadonlySet<string>
  ): boolean {
    const owners = this.clients.get(client);
    const key = hashKey(queryKey);
    const owner = owners?.get(key);
    if (!owner) {
      return true;
    }
    if (reasons.size === 1 && reasons.has("event") && owner.isFresh()) {
      return false;
    }
    owners?.delete(key);
    owner.yield();
    return true;
  }
}
